import { flattenErrorTypeArray } from '@equifinance/shared';
import { collectIdFieldValues } from '../../utils/errors';
import { SectionTitleIcon } from '../../Icons/SectionTitleIcon';
import { PaddedSection } from '../../components/Section';
import useDeleteApplicant from '../../hooks/useDeleteApplicant';
import { AccountButton, AccountButtonContainer } from '../../components/Buttons';
import { StaticDataItem } from '../../../../api/useAccountApi';
import { Applicant } from '../../../../contexts/account/Account';
import { Stages } from '../../../../types/Stages';
import { stageOnOrAfter } from '../../../../contexts/helpers';
import PersonalDetails from './PersonalDetails';
import AddressHistory from './AddressHistory';
import ApplicantContactDetails from './ApplicantContactDetails';
import AdditionalSupport from './AdditionalSupport';
import { useAccountContext } from '../../../../contexts';
import IncomeAndEmploymentSection from './IncomeAndEmployment/IncomeAndEmploymentSection';

export default function ApplicantSection({
  applicant,
  applicantIndex,
  countries,
  lendingRegions,
  ukCountries,
  accountancyQualifications,
  industries,
  jobTitles,
  numberOfApplicants,
}: {
    applicant: Applicant,
    applicantIndex: number,
    countries: StaticDataItem[],
    lendingRegions: StaticDataItem[],
    ukCountries: StaticDataItem[],
    accountancyQualifications: StaticDataItem[],
    industries: StaticDataItem[],
    jobTitles: StaticDataItem[],
    numberOfApplicants: number,
  }) {
  const { account, creditSearchCompleted } = useAccountContext();

  const {
    stage, errors, warnings, applicants,
  } = account;

  const {
    incomeAndEmployment,
    personalDetails,
    contacts,
    additionalSupportDetails,
    addresses,
  } = applicant;

  const { contactDetails = [] } = contacts ?? {};
  const contactDetailsErrors = contactDetails?.flatMap((cd) => errors?.[cd?.id] ?? []) ?? [];

  const { employmentHistory = [] } = incomeAndEmployment ?? {};
  const applicantWorkHistoryErrors = employmentHistory?.flatMap((eh) => [...collectIdFieldValues(eh).flatMap((id) => errors?.[id] ?? [])]) ?? [];

  const listAddressErrors = addresses?.map(
    (address) => errors?.[address?.address?.id],
  )?.flat()?.filter((v) => !!v) ?? [];

  const listAddressesErrors = addresses?.map(
    (address) => errors?.[address?.id],
  )?.flat()?.filter((v) => !!v) ?? [];

  const onClickDeleteApplicant = useDeleteApplicant();

  const combinedErrors = flattenErrorTypeArray([
    ...(errors?.[incomeAndEmployment?.id] ?? []),
    ...(errors?.[personalDetails?.id] ?? []),
    ...(errors?.[contacts?.id] ?? []),
    ...contactDetailsErrors,
    ...(errors?.[additionalSupportDetails?.id] ?? []),
    ...(errors?.[applicant?.id] ?? []),
    ...listAddressErrors,
    ...listAddressesErrors,
    ...applicantWorkHistoryErrors,
  ]);

  return (
    <PaddedSection
      id={applicant.id}
      refName={`${applicant.id}-${applicantIndex}`}
      key={`${applicant.id}-applicant`}
      title={`Applicant ${numberOfApplicants > 1 ? applicantIndex + 1 : ''}`}
      titleIcon={(
        <SectionTitleIcon
          errors={combinedErrors}
          isLocked={
            (stage === Stages.CreditDecision
              || stage === Stages.Affordability)
            && creditSearchCompleted
          }
        />
      )}
      errors={errors}
      warnings={warnings}
    >
      {(applicants.length > 1 && !creditSearchCompleted) && (
        <AccountButtonContainer>
          <AccountButton onClick={() => onClickDeleteApplicant(applicant, applicantIndex)}>Delete Applicant</AccountButton>
        </AccountButtonContainer>
      )}

      <PersonalDetails
        applicant={applicant}
        index={applicantIndex}
      />

      {stageOnOrAfter(stage, Stages.Packaging) && (
        <ApplicantContactDetails
          applicant={applicant}
          applicantIndex={applicantIndex}
        />
      )}

      <AddressHistory
        applicant={applicant}
        applicantIndex={applicantIndex}
        lendingRegions={lendingRegions}
        countries={countries}
      />

      {stageOnOrAfter(stage, Stages.Packaging) && (
      <>
        <AdditionalSupport
          applicant={applicant}
          applicantIndex={applicantIndex}
        />

        <IncomeAndEmploymentSection
          applicant={applicant}
          applicantIndex={applicantIndex}
          ukCountries={ukCountries}
          accountancyQualifications={accountancyQualifications}
          industries={industries}
          jobTitles={jobTitles}
        />
      </>
      )}

    </PaddedSection>
  );
}
